import React, { useContext } from "react";
import styled from "styled-components";
import {
    BusinessPortalHeader,
    PAYMENT_FLOW_MAX_WIDTH,
    PortalCaption,
    PortalFormActions,
    PortalFormScrollBody,
    PortalFormSection,
    PortalMetricCard,
    PortalMetricLabel,
    PortalPaymentSummaryAmount,
    PortalPaymentSummaryCard,
    PortalPaymentSummaryLabel,
    PortalPaymentSummaryRow,
    PortalPanelHeader,
    PortalSectionTitle,
    PortalFormSurface,
    StandardModalActionButton,
    StandardModalActionGroup
} from "src/components";
import { SubscriptionContext } from "../SubscriptionContext";
import { formatCurrency } from "src/utils/currency";
import { AUSTRALIA } from "src/constants/subscriptions";

const PanelStack = styled(PortalFormSection)`
    width: 100%;
    display: grid;
    gap: 1rem;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const DetailCard = styled(PortalMetricCard)`
    min-height: auto;
    align-content: start;
    gap: 0.4rem;
`;

const SubscriptionOverview = ({
    totalNumber,
    goToPayment,
    pricing,
}) => {
    const context = useContext(SubscriptionContext);
    const { companyName, abn, country, backToPrevious, isPurchasingMoreUsers } = context;

    const plan = pricing?.plan;
    const { pricePerUserDisplay, subTotal, gst, processingFee, totalAmount, perPeriodSlashSuffix, perFullDurationSlashSuffix, currency } = pricing || {};

    const proceedToPayment = e => {
        goToPayment();
    };

    return (
        <React.Fragment>
            <BusinessPortalHeader
                title={!isPurchasingMoreUsers ? "Subscription Overview: Step 4" : "Purchase More User Licenses"}
                description="Here is an overview of your business setup and user license selection. View your order summary and click proceed to payment to purchase your subscription."
                maxWidth={PAYMENT_FLOW_MAX_WIDTH}
            />
            <PortalFormSurface maxWidth={PAYMENT_FLOW_MAX_WIDTH} marginTop="0" flex="1 1 auto" minHeight="0">
                <PortalFormScrollBody columns="repeat(2, minmax(0, 1fr))" padding="1.25rem" gap="1rem">
                    <PanelStack>
                        <PortalPanelHeader withDivider>
                            <div>
                                <PortalSectionTitle>
                                    { isPurchasingMoreUsers ? "Purchase Overview" : "Subscription Overview" }
                                </PortalSectionTitle>
                                <PortalCaption margin="0.25rem 0 0">
                                    Review the selected business and license details before payment.
                                </PortalCaption>
                            </div>
                        </PortalPanelHeader>

                        <DetailGrid>
                            <DetailCard minHeight="auto">
                                <PortalMetricLabel>Business Name</PortalMetricLabel>
                                <PortalSectionTitle size="1rem">{companyName}</PortalSectionTitle>
                            </DetailCard>

                            { country === AUSTRALIA &&
                                <DetailCard minHeight="auto">
                                    <PortalMetricLabel>ABN</PortalMetricLabel>
                                    <PortalSectionTitle size="1rem">{abn}</PortalSectionTitle>
                                </DetailCard>
                            }

                            <DetailCard minHeight="auto">
                                <PortalMetricLabel>Country</PortalMetricLabel>
                                <PortalSectionTitle size="1rem">{country}</PortalSectionTitle>
                            </DetailCard>

                            <DetailCard minHeight="auto">
                                <PortalMetricLabel>User Licenses</PortalMetricLabel>
                                <PortalSectionTitle size="1rem">{totalNumber}</PortalSectionTitle>
                            </DetailCard>
                        </DetailGrid>

                        <DetailCard minHeight="auto">
                            <PortalMetricLabel>Subscription Type</PortalMetricLabel>
                            <PortalSectionTitle size="1rem">{plan?.name || ""}</PortalSectionTitle>
                            <PortalCaption margin="0">{plan?.paymentDescription || ""}</PortalCaption>
                        </DetailCard>
                    </PanelStack>

                    <PanelStack>
                        <PortalPanelHeader withDivider>
                            <div>
                                <PortalSectionTitle>Order Summary</PortalSectionTitle>
                                <PortalCaption margin="0.25rem 0 0">
                                    Confirm the subscription cost for this purchase.
                                </PortalCaption>
                            </div>
                        </PortalPanelHeader>

                        <DetailCard minHeight="auto">
                            <PortalMetricLabel>Subscription Duration</PortalMetricLabel>
                            <PortalSectionTitle size="1rem">{plan?.name || ""}</PortalSectionTitle>
                        </DetailCard>

                        <DetailCard minHeight="auto">
                            <PortalMetricLabel>Number of User Licenses</PortalMetricLabel>
                            <PortalSectionTitle size="1rem">{totalNumber}</PortalSectionTitle>
                        </DetailCard>

                        <DetailCard minHeight="auto">
                            <PortalMetricLabel>Price Per User License</PortalMetricLabel>
                            <PortalSectionTitle size="1rem">
                                {formatCurrency(pricePerUserDisplay, currency)}/per {(perPeriodSlashSuffix || "").toLowerCase()}
                            </PortalSectionTitle>
                        </DetailCard>

                        <PortalPaymentSummaryCard>
                            <PortalPaymentSummaryRow>
                                <PortalPaymentSummaryLabel>Subtotal</PortalPaymentSummaryLabel>
                                <PortalPaymentSummaryAmount>
                                    {formatCurrency(subTotal, currency)}/{perFullDurationSlashSuffix}
                                </PortalPaymentSummaryAmount>
                            </PortalPaymentSummaryRow>
                            { processingFee > 0 &&
                                <PortalPaymentSummaryRow>
                                    <PortalPaymentSummaryLabel>Processing Fee</PortalPaymentSummaryLabel>
                                    <PortalPaymentSummaryAmount weight={700} size="0.875rem">
                                        {formatCurrency(processingFee, currency)}/{perFullDurationSlashSuffix}
                                    </PortalPaymentSummaryAmount>
                                </PortalPaymentSummaryRow>
                            }
                            { gst > 0 &&
                                <PortalPaymentSummaryRow>
                                    <PortalPaymentSummaryLabel>GST</PortalPaymentSummaryLabel>
                                    <PortalPaymentSummaryAmount weight={700} size="0.875rem">
                                        {formatCurrency(gst, currency)}/{perFullDurationSlashSuffix}
                                    </PortalPaymentSummaryAmount>
                                </PortalPaymentSummaryRow>
                            }
                            <PortalPaymentSummaryRow>
                                <PortalPaymentSummaryLabel>Total</PortalPaymentSummaryLabel>
                                <PortalPaymentSummaryAmount size="1.25rem">
                                    {formatCurrency(totalAmount, currency, true)}/{perFullDurationSlashSuffix}
                                </PortalPaymentSummaryAmount>
                            </PortalPaymentSummaryRow>
                        </PortalPaymentSummaryCard>
                    </PanelStack>
                </PortalFormScrollBody>

                <PortalFormActions sticky={false}>
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton type="button" onClick={() => backToPrevious()}>
                            Back
                        </StandardModalActionButton>
                        <StandardModalActionButton type="button" $variant="primary" minWidth="11.125rem" width="11.125rem" onClick={(e) => proceedToPayment(e)}>
                            Proceed to Payment
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                </PortalFormActions>
            </PortalFormSurface>
        </React.Fragment>
    );
};

export default SubscriptionOverview;
